import React, { useState } from 'react';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography,
  Menu, MenuItem, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Business as BusinessIcon,
  CreditCard as BillingIcon, ReceiptLong as InvoiceIcon,
  AccountCircle, Logout, Menu as MenuIcon,
  People as PeopleIcon, Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import NotificationBell from '../components/NotificationBell';
import ThemeSettingsButton from '../components/ThemeSettingsButton';

const drawerWidth = 240;


const menuItems = [
  { text: 'Tenant Overview', icon: <DashboardIcon />, path: '/tenant', roles: ['tenant_admin', 'tenant_support', 'tenant_billing'] },
  { text: 'Companies', icon: <BusinessIcon />, path: '/tenant/companies', roles: ['tenant_admin', 'tenant_support'] },
  { text: 'Billing', icon: <BillingIcon />, path: '/tenant/billing', roles: ['tenant_admin', 'tenant_billing'] },
  { text: 'Invoices', icon: <InvoiceIcon />, path: '/tenant/invoices', roles: ['tenant_admin', 'tenant_billing'] },
  { text: 'Team', icon: <PeopleIcon />, path: '/tenant/users', roles: ['tenant_admin'] },
  { text: 'Settings', icon: <SettingsIcon />, path: '/tenant/settings', roles: ['tenant_admin', 'tenant_support', 'tenant_billing'] },
];

interface TenantLayoutProps { children: React.ReactNode; }

const TenantLayout: React.FC<TenantLayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { user, logout } = useAuth();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const drawer = (
    <div>
      <Box sx={{
        p: 1.5, borderBottom: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        bgcolor: mode === 'dark' ? '#1e1e1e' : '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        minHeight: '75px', justifyContent: 'center',
      }}>
        <img
          src={mode === 'dark' ? '/Scetru-Logo-white.png' : '/Scetru-Logo-black.png'}
          alt="Scetru"
          style={{ height: '50px', width: 'auto', maxWidth: '95%', objectFit: 'contain' }}
        />
        <Typography variant="caption" sx={{
          fontWeight: 600, color: mode === 'dark' ? '#b0b0b0' : 'rgba(0,0,0,0.6)',
        }}>
          Tenant Admin
        </Typography>
      </Box>

      <List sx={{ pt: 1 }}>
        {menuItems
          .filter(item => item.roles.includes(user?.role || ''))
          .map(item => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e6f2ff',
                    borderRight: '3px solid', borderRightColor: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                    '& .MuiListItemText-primary': { color: 'primary.main', fontWeight: 600 },
                  },
                  '&:hover': { backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: mode === 'dark' ? '#fff' : 'rgba(0,0,0,0.7)' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar position="fixed" sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        backgroundColor: 'primary.main',
        zIndex: (t) => t.zIndex.drawer + 1,
        '& .MuiIconButton-root': { color: '#fff !important' },
        '& .MuiTypography-root': { color: '#fff !important' },
      }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton edge="start" onClick={() => setMobileOpen(o => !o)}
            sx={{ display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <ThemeSettingsButton />
            <NotificationBell />
            <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <AccountCircle />
            </IconButton>
          </Box>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem disabled><Typography variant="body2">{user?.firstName} {user?.lastName}</Typography></MenuItem>
            <MenuItem disabled><Typography variant="body2" sx={{ color: 'text.secondary' }}>{user?.email}</Typography></MenuItem>
            <Divider />
            <MenuItem onClick={async () => { await logout(); navigate('/login'); }}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth, backgroundColor: mode === 'dark' ? '#1e1e1e' : '#fff' },
          }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth, borderRight: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              backgroundColor: mode === 'dark' ? '#1e1e1e' : '#fff',
            },
          }}
          open>
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{
        flexGrow: 1, p: { xs: 2, sm: 3 },
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        minHeight: '100vh',
        backgroundColor: mode === 'dark' ? '#121212 !important' : '#f5f5f5 !important',
        '& .MuiPaper-root': mode === 'dark'
          ? { backgroundColor: '#1e1e1e !important', color: '#fff !important' }
          : { backgroundColor: '#fff !important', color: '#000 !important' },
      }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default TenantLayout;